'use client'

import SubCategoriesSliderSkeleton from '@/components/assets-explorer/subcategories-slider-skeleton'
import React, { useCallback, useMemo, useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import client from '@/lib/api/client'
import { SubCategory } from '@/lib/categories/categories'
import { Carousel, CarouselApi, useCarousel } from '@/components/ui/carousel'
import useScrollToCarouselSelectedItem from '@/lib/hooks/useScrollToCarouselSelectedItem'
import { ImageCard } from '@/components/assets-explorer/image-card'
import { WheelGesturesPlugin } from 'embla-carousel-wheel-gestures'
import { cn } from '@/lib/utils'
import { useParams } from 'next/navigation'

type SubCategoriesSliderCarouseContentProps = {
  subCategories: SubCategory[]
  subCategorySlug?: string
  onItemSelect?: (subCategorySlug: string) => void
}

const SubCategoriesSliderCarouseContent = ({
  subCategories,
  subCategorySlug,
  onItemSelect,
}: SubCategoriesSliderCarouseContentProps) => {
  const { carouselRef } = useCarousel()

  return (
    <div ref={carouselRef}>
      <ul className={cn('tw--ml-2 tw-flex')}>
        {subCategories?.map((subCategory) => (
          <li
            key={`subcategories-slider-${subCategory.slug}`}
            role="group"
            aria-roledescription="slide"
            className={cn(
              'carousel-item tw-z-[1] tw-min-w-0 tw-max-w-max tw-shrink-0 tw-grow-0 tw-basis-full tw-pl-2 hover:tw-z-[10]'
            )}
          >
            <ImageCard
              className={cn('tw-bg-[var(--Primary-400)] tw-transition-all tw-duration-200 hover:tw-opacity-100', {
                // 'tw-opacity-70 hover:tw-scale-[var(--scale-hover-outer)] hover:tw-opacity-100 hover:tw-shadow-[0_0_20px_2px_rgba(0,0,0,1)]':
                //   subCategory.slug !== subCategorySlug,
              })}
              title={subCategory.name}
              link={`/${subCategory.mainCategorySlug}/${subCategory.categorySlug}/${subCategory.slug}`}
              scroll={false}
              size="sm"
              selected={subCategory.slug === subCategorySlug}
              onLinkClick={() => {
                onItemSelect?.(subCategory.slug)
              }}
            />
          </li>
        ))}
      </ul>
    </div>
  )
}

export type SubCategoriesSliderProps = {
  mainCategorySlug: string
  categorySlug: string
  subCategorySlug?: string
  subCategories?: SubCategory[]
  // When true, we will try to get subCategory with the slug provided in the URL
  fallbackCategorySlug?: boolean
  className?: string
}

export default function SubCategoriesSlider({
  mainCategorySlug,
  categorySlug,
  subCategorySlug: initialSubCategorySlug,
  subCategories: initialSubCategories,
  fallbackCategorySlug = false,
  className,
}: SubCategoriesSliderProps) {
  const [api, setApi] = React.useState<CarouselApi>()

  const { subCategory: pathSubCategory } = useParams<{ subCategory?: string }>()

  const resolvedSubCategorySlug = fallbackCategorySlug
    ? initialSubCategorySlug || pathSubCategory
    : initialSubCategorySlug

  const [subCategorySlug, setSubCategorySlug] = React.useState<string | undefined>(resolvedSubCategorySlug)
  const [scrollToSubCategorySlug, setScrollToSubCategorySlug] = useState<string | undefined>(resolvedSubCategorySlug)

  const { data: subCategories, isLoading: isLoadingSubCategories } = useQuery({
    queryKey: ['subcategories', mainCategorySlug, categorySlug],
    queryFn: async ({ signal }) => {
      const { data } = await client.get<SubCategory[]>(`/subcategories`, {
        params: {
          main_category: mainCategorySlug,
          category: categorySlug,
        },
        signal,
      })
      return data
    },
    initialData: initialSubCategories,
  })

  const initiallySelectedSubCategoryIndex = useMemo(() => {
    return subCategories?.findIndex((content) => content.slug === scrollToSubCategorySlug) ?? -1
  }, [scrollToSubCategorySlug, subCategories])

  const onScroll = useCallback(() => {
    setScrollToSubCategorySlug(undefined)
  }, [])

  useScrollToCarouselSelectedItem({
    api,
    itemIndex: initiallySelectedSubCategoryIndex,
    onScroll,
  })

  // <div className="tw-mx-2 tw-mb-6 tw-overflow-hidden">{subcategoriesSlider}</div>

  return (
    <div data-slot="subcategories-slider" className={cn('subcategories-slider tw-w-full', className)}>
      {isLoadingSubCategories && <SubCategoriesSliderSkeleton />}

      {!isLoadingSubCategories && (
        <Carousel
          setApi={setApi}
          opts={{
            align: 'start',
            skipSnaps: true,
          }}
          plugins={[
            WheelGesturesPlugin({
              forceWheelAxis: 'x',
            }),
          ]}
          className="subcategories-slider-carousel tw-duration-500 tw-animate-in tw-fade-in"
        >
          <SubCategoriesSliderCarouseContent
            subCategories={subCategories ?? []}
            subCategorySlug={subCategorySlug}
            onItemSelect={(slug) => {
              setSubCategorySlug(slug)
            }}
          />
        </Carousel>
      )}
    </div>
  )
}
